import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button } from '@rematter/ui-web';
import {
  DataGrid,
  DataGridColumn,
  HeaderIcons,
  Pagination,
  type PaginationProps,
  Badge,
} from '@rematter/ui-web';
import {
  Content,
  Footer,
  Toolbar,
  FloatingToolbar,
  Separator,
} from '@rematter/ui-web';
import { SearchField } from '@rematter/ui-web';
import { Loading } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';
import { PlusCircle, MoreHorizontal, Trash } from 'lucide-react';

import { TransportationTypeDialog } from './TransportationTypeDialog';

import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/transportation/_state/transportationTypesList.state';
import { useDeleteTransportationTypeMutation } from '~/modules/transportation/hooks/mutations/useDeleteTransportationTypeMutation';
import { useTransportationTypesQuery } from '~/modules/transportation/hooks/queries/useTransportationTypesQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { TransportationTypeRecord } from '~/modules/transportation/types';
import useDebouncedValue from '~/shared/hooks/useDebounce';

export const TransportationTypesListView: React.FC = () => {
  const t = useTranslations();
  const toast = useToast();
  const [selectedRows, setSelectedRows] = React.useState<
    TransportationTypeRecord[]
  >([]);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isCreateFormOpen, setIsCreateFormOpen] = React.useState(false);
  const [editType, setEditType] =
    React.useState<TransportationTypeRecord | null>(null);

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const { data, error, isLoading, refetch } = useTransportationTypesQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit,
    offset,
  });

  const { mutateAsync: deleteTransportationType } =
    useDeleteTransportationTypeMutation();

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const handleEdit = (type: TransportationTypeRecord) => {
    setEditType(type);
    setIsCreateFormOpen(true);
  };

  const handleDelete = async () => {
    try {
      await Promise.all(
        selectedRows.map((row) => deleteTransportationType({ id: row.id })),
      );
      toast.success(
        t.transportationTypes.messages.deleteSuccess(selectedRows.length),
      );
      setSelectedRows([]);
      refetch();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.transportationTypes.messages.error,
      );
    }
  };

  const COLUMNS: DataGridColumn<TransportationTypeRecord>[] = [
    {
      key: 'name',
      header: t.transportationTypes.table.columns.name,
      width: 250,
      icon: HeaderIcons.Text,
      sortable: true,
      cell: ({ row }) => row.name,
    },
    {
      key: 'description',
      header: t.transportationTypes.table.columns.description,
      grow: 1,
      icon: HeaderIcons.Text,
      cell: ({ row }) => row.description,
    },
    {
      key: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          className="border border-neutral/40 shadow-sm shadow-neutral/10"
          onPress={() => handleEdit(row)}
          size="small"
        >
          <MoreHorizontal size={16} color="black" />
        </Button>
      ),
    },
  ];

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.transportationTypes.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  const types: TransportationTypeRecord[] = data?.transportationTypes ?? [];

  return (
    <TransportationRootLayout className="@TransportationTypesListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.transportationTypes.toolbar.search}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onPress={() => {
              setEditType(null);
              setIsCreateFormOpen(true);
            }}
            variant="solid"
            color="primary"
            icon={<PlusCircle />}
          >
            {t.transportationTypes.toolbar.create}
          </Button>
        </div>
      </Toolbar>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          selected
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<Trash />}
          isDisabled={selectedRows.length < 1}
          onPress={handleDelete}
        >
          {t.transportationTypes.toolbar.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<TransportationTypeRecord>
            columns={COLUMNS}
            dataSource={types}
            rowKey="id"
            aria-label={t.transportationTypes.table.aria}
            selectionMode="multiple"
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                setSelectedRows(types);
              } else {
                const selectedTypes = types.filter((type) =>
                  keys.has(String(type.id)),
                );
                setSelectedRows(selectedTypes);
              }
            }}
            className="w-full text-sm"
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={data?.total ?? 0}
          onChange={handlePaginationChange}
        />
      </Footer>

      <TransportationTypeDialog
        isOpen={isCreateFormOpen}
        onClose={() => {
          setIsCreateFormOpen(false);
          setEditType(null);
        }}
        onSuccess={() => {
          setIsCreateFormOpen(false);
          setEditType(null);
          refetch();
        }}
        transportationType={editType ?? undefined}
      />
    </TransportationRootLayout>
  );
};
